import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GraphService_DN from '../../../services/GraphService_DN';
import { getRegions, getYears } from '../../../interfaces/traffic_accidents/FormControllerData';
import { czechRegionPaths } from '../../../data/CzechRegionPaths';
import SelectionData from '../../../interfaces/Graphs/SelectionData';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { MdLocalPolice } from 'react-icons/md';
import css from '../TReportCenter/Report.module.css';

interface RegionData {
  name: string;
  count: number;
  percent: number;
}

export default function TRegionMap() {
  const { t } = useTranslation("traffic");
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [regionsData, setRegionsData] = useState<{ [key: string]: RegionData }>({});
  const [loading, setLoading] = useState(true);
  const [totalAccidents, setTotalAccidents] = useState(0);
  const [selectedYear, setSelectedYear] = useState<string>('2025');
  const [years] = useState<SelectionData[]>(getYears().filter(year => year.Code !== '2016'));

  // Mapa medzi SVG ID a API kódy
  const regionMapping: { [key: string]: string } = {
    "CZ10": "0",  // Praha
    "CZ20": "1",  // Středočeský
    "CZ31": "2",  // Jihočeský
    "CZ32": "3",  // Plzeňský
    "CZ42": "4",  // Ústecký
    "CZ52": "5",  // Královéhradecký
    "CZ64": "6",  // Jihomoravský
    "CZ80": "7",  // Moravskoslezský
    "CZ71": "8",  // Olomoucký
    "CZ72": "9",  // Zlínský
    "CZ63": "10", // Vysočina
    "CZ53": "11", // Pardubický
    "CZ51": "12", // Liberecký
    "CZ41": "13"  // Karlovarský
  };

  useEffect(() => {
    loadAccidentData();
  }, [selectedYear]);

  const loadAccidentData = async () => {
    setLoading(true);
    try {
      const response = await GraphService_DN.FetchDataFrom_API(
        `/Graph/animals-per-year/${selectedYear}`
      );
      
      const regions = getRegions();
      const regionCounts: { [code: string]: number } = {};
      
      regions.forEach(region => {
        regionCounts[region.Code] = 0;
      });
      
      // API vráti pole 14 objektov: { Month, Year, Region (názov), Count }
      if (response.data && Array.isArray(response.data)) {
        response.data.forEach((item: any) => {
          const regionName = item.Region || item.region;
          const count = item.Count || item.count || 0;
          
          const region = regions.find(r => r.Type === regionName);
          
          if (region && regionCounts.hasOwnProperty(region.Code)) {
            regionCounts[region.Code] = count;
          }
        });
      }

      
      const total = Object.values(regionCounts).reduce((sum, count) => sum + count, 0);
      console.log('Total accidents:', total);
      setTotalAccidents(total);


      const dataMap: { [key: string]: RegionData } = {};
      regions.forEach(region => {
        dataMap[region.Code] = {
          name: region.Type,
          count: regionCounts[region.Code],
          percent: total > 0 ? (regionCounts[region.Code] / total) * 100 : 0
        };
      });

      setRegionsData(dataMap);
    } catch (error) {
      console.error('Chyba pri načítavaní dát:', error);
    } finally {
      setLoading(false);
    }
  };

  const getColorForPercent = (percent: number): string => {
    if (percent === 0) return "#e0e0e0"; // Šedá pre 0
    if (percent <= 2) return "#32cd32";  // Zelená
    if (percent <= 6) return "#007500";  // Tmavo zelená
    if (percent <= 10) return "#ffcc00"; // Svetlejšia žltá
    if (percent <= 15) return "#ff6b00"; // Tmavo oranžová
    return "#cc0000";                     // Tmavšia červená >15%
  };

  return (
    <div className="box-12" style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>
        {t("Map.regionMapNadpis")} ({selectedYear})
      </h2>

        <div style={{ position: 'relative', minHeight: '600px' }}>
          {loading ? (
            <div className={css.loading}>
              <div><MdLocalPolice /> {t("Traffic.loading")}</div>
            </div>
          ) : (
            <>
              <div style={{ position: 'relative', width: '100%', maxWidth: '1000px', margin: '0 auto' }}>
              <div style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 100, backgroundColor: 'white', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <FormControl style={{ minWidth: 110 }} size="small">
                  <InputLabel id="year-select-label">{t("Map.rok")}</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={selectedYear}
                    label={t("Map.rok")}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {years.map((year) => (
                      <MenuItem key={year.Code} value={year.Code}>
                        {year.Type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              
              <svg
                viewBox="0 0 1000 570"
                style={{ width: '100%', height: 'auto', border: '1px solid #ddd' }}
              >
                {Object.entries(czechRegionPaths).map(([regionId, pathData]) => {
                  const apiCode = regionMapping[regionId as keyof typeof regionMapping];
                  const isHovered = hoveredRegion === apiCode;
                  const baseColor = regionsData[apiCode] ? getColorForPercent(regionsData[apiCode].percent) : "#e0e0e0";
                  
                  return (
                    <path
                      key={regionId}
                      d={pathData}
                      id={regionId}
                      fill={baseColor}
                      stroke="#ffffff"
                      strokeWidth="0.5"
                      style={{ 
                        cursor: 'pointer', 
                        transition: 'filter 0.2s',
                        filter: isHovered ? 'brightness(0.7)' : 'none'
                      }}
                      onMouseEnter={() => setHoveredRegion(apiCode)}
                      onMouseLeave={() => setHoveredRegion(null)}
                    />
                  );
                })}
              </svg>

              {/* Tooltip */}
              {hoveredRegion && (
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'white',
                    padding: '15px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    zIndex: 1000
                  }}
                >
                  {regionsData[hoveredRegion] ? (
                    <>
                      <strong>{regionsData[hoveredRegion].name}</strong>
                      <div>{t("Map.pocet")}:   {regionsData[hoveredRegion].count}</div>
                      <div>{t("Map.podiel")}: {regionsData[hoveredRegion].percent.toFixed(2)} %</div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MdLocalPolice style={{ animation: 'spin 2s linear infinite' }} />
                      {t("Traffic.loading")}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Legenda */}
            <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <h3>{t("Map.legenda")}:</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#32cd32' }}></div>
                  <span>≤ 2%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#007500' }}></div>
                  <span>2-6%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#ffcc00' }}></div>
                  <span>6-10%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#ff6b00' }}></div>
                  <span>10-15%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#cc0000' }}></div>
                  <span>{'>'} 15%</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '20px', height: '20px', backgroundColor: '#e0e0e0' }}></div>
                  <span>{t("Map.noData")}</span>
                </div>
              </div>
            </div>

            {/* Štatistiky */}
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <h3 style={{ marginBottom: '15px' }}>{t("Map.statistiky")} ({selectedYear}):</h3>
              <p style={{ marginBottom: '8px' }}>
                <strong>{t("Map.celkovyPocet")}:</strong> {totalAccidents.toLocaleString()}
              </p>
              <p style={{ marginBottom: '8px' }}>
                <strong>{t("Map.priemernyPocet")}:</strong> {Math.round(totalAccidents / Object.keys(regionsData).length).toLocaleString()}
              </p>
              
              {(() => {
                const sortedRegions = Object.entries(regionsData)
                  .filter(([_, data]) => data.count > 0)
                  .sort((a, b) => a[1].count - b[1].count);
                
                const minRegion = sortedRegions[0];
                const maxRegion = sortedRegions[sortedRegions.length - 1];
                
                return (
                  <>
                    {minRegion && (
                      <p style={{ marginBottom: '8px' }}>
                        <strong>{t("Map.krajNajmenej")}:</strong> {minRegion[1].name}
                      </p>
                    )}
                    {maxRegion && (
                      <p style={{ marginBottom: '50px' }}>
                        <strong>{t("Map.krajNajviac")}:</strong> {maxRegion[1].name} 
                      </p>
                    )}
                  </>
                );
              })()}
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '8px' }}>
                {Object.entries(regionsData)
                  .sort((a, b) => b[1].count - a[1].count)
                  .map(([code, data]) => (
                    <div key={code} style={{ padding: '5px', backgroundColor: 'white', borderRadius: '3px' }}>
                      <strong>{data.name}:</strong> {data.count.toLocaleString()} ({data.percent.toFixed(2)}%)
                    </div>
                  ))}
              </div>
            </div>
            </>
          )}
        </div>
      </div>
  );
}